import type { ComponentChildren } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import { resetCursors, startSync } from '../sync/sync'
import { setSyncStatus } from '../sync/status'
import { currentMembership, forgetMembership, resolveMembership } from './household'
import { LoginScreen } from './LoginScreen'
import { isSyncConfigured, supabase } from './supabase'

type Gate = 'checking' | 'household' | 'in' | 'out'

/**
 * Decides whether to show the app, the login screen, or neither yet.
 *
 * Ported from `../semainier/src/auth/AuthGate.tsx`, whose offline rules are the
 * point of the file and are kept exactly:
 *
 *   - a stored session opens the app immediately, with no network call on the
 *     critical path;
 *   - a failed token refresh never clears local data and never forces a
 *     logout, it just leaves sync reporting an error;
 *   - only an explicit sign-out returns to the login screen.
 *
 * The step semainier does not have is resolving the household, and it has to
 * come *before* the app renders rather than alongside it: the member id is a
 * component of every completion id, so a device that ticked a box without one
 * would write rows no other device could ever match. It is resolved once and
 * cached, so this only blocks on the very first start after signing in.
 *
 * With no backend configured the gate is transparent: local-only, always
 * available, no login and no network.
 */
export function AuthGate({ children }: { children: ComponentChildren }) {
  const [gate, setGate] = useState<Gate>(isSyncConfigured ? 'checking' : 'in')
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (!isSyncConfigured || !supabase) {
      setSyncStatus({ phase: 'disabled' })
      return
    }

    const client = supabase
    let stopSync: (() => void) | undefined
    let cancelled = false

    function enter() {
      if (cancelled) return
      setGate('in')
      stopSync ??= startSync()
    }

    /**
     * Ask the server for this account's household, or take the cached answer.
     *
     * `displayName` is offered only for the row `ensure_household` may have to
     * create; it is ignored for an account that is already a member, which is
     * why signing in on a second device never renames anybody.
     */
    function admit(displayName: string) {
      if (currentMembership()) {
        enter()
        return
      }

      setGate('household')
      setError(null)
      resolveMembership(displayName)
        .then(enter)
        .catch((err: unknown) => {
          if (cancelled) return
          setError(err instanceof Error ? err.message : String(err))
        })
    }

    // getSession reads the persisted session without a network round trip, so
    // this resolves offline just as well as online.
    void client.auth.getSession().then(({ data }) => {
      if (cancelled) return
      if (!data.session) {
        setGate('out')
        return
      }
      admit(nameFromEmail(data.session.user.email))
    })

    const { data: listener } = client.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        setGate('out')
        // The binding to the account goes; the data stays. A sign-out that
        // dropped local rows would lose whatever had not been pushed yet.
        resetCursors()
        forgetMembership()
        stopSync?.()
        stopSync = undefined
        return
      }

      if (session) admit(nameFromEmail(session.user.email))
    })

    return () => {
      cancelled = true
      listener.subscription.unsubscribe()
      stopSync?.()
    }
    // `attempt` is here so the *Réessayer* button re-runs the whole effect,
    // including the session read — the failure it retries is usually a network
    // one, and by then the session may need refreshing too.
  }, [attempt])

  if (gate === 'checking') {
    return <main class="loading">Maison Zen se prépare… 🏡</main>
  }

  if (gate === 'household') {
    return (
      <main class="login-screen">
        <section class="card login-card">
          <p class="eyebrow">MAISON ZEN</p>
          {error ? (
            <>
              <h2>Connexion au foyer impossible</h2>
              <p class="login-hint">
                Tes cases restent sur cet appareil. Vérifie ta connexion et réessaie.
              </p>
              <p class="login-error">{error}</p>
              <button class="primary" type="button" onClick={() => setAttempt((n) => n + 1)}>
                Réessayer
              </button>
            </>
          ) : (
            <p class="login-hint">Ouverture du foyer…</p>
          )}
        </section>
      </main>
    )
  }

  if (gate === 'out') return <LoginScreen />

  return <>{children}</>
}

/** A first name to put on the member row, for the day the household has two. */
function nameFromEmail(email: string | undefined): string {
  const local = (email ?? '').split('@')[0] ?? ''
  return local ? local.charAt(0).toUpperCase() + local.slice(1) : ''
}
